import type {CreateExtArgs} from '@wix/document-manager-core'
import type {IdMapValue, UploadResponse, SyncImportFileResponse, MediaBatchContext} from '@wix/document-services-types'
import {OperationStatus} from '../../constants/dsTypesConstants'
import {Handler, HandlerResult, HandlerStatus, PayloadData, PayloadStatus} from './eventHandlerExtension'
import type {FileUploadExtApi, UploadFilesSyncObject} from './fileUploadExtension'
import {uploadTimeoutReason, aiImageDescriptorUrl, ERRORS} from './constants'
import {getReportableFromError} from '@wix/document-manager-utils'
import type {LoggerExtAPI} from '../logger'

interface FileStatusResult {
    status: string | undefined
    fileDescriptor: any
}

const checkFileStatus = async (serverFacade: any, fileId: string): Promise<FileStatusResult> => {
    const descriptorOutput: UploadResponse = await serverFacade.fetch(`${aiImageDescriptorUrl}?fileId=${fileId}`, 'GET')
    return {
        status: descriptorOutput?.file?.operationStatus,
        fileDescriptor: descriptorOutput
    }
}

export interface UploadConfig<TTransformed> {
    extractData(output: UploadResponse): any | undefined
    transformData(data: any): Promise<TTransformed> | TTransformed
    injectItem(createExtArgs: CreateExtArgs, idMapValue: IdMapValue, data: TTransformed): void
    errorMessage: string
    timeOutInMS?: number
    onItemUploadStart?(url: string): void
    onItemUploadComplete?(url: string): void
}

const checkUploadCompletionAndCallbacks = <TOriginalData>(context: MediaBatchContext<TOriginalData>, result?: any) => {
    const {processedItems, totalItems, failures, onError, onSuccess, createExtArgs} = context

    if (processedItems === totalItems) {
        createExtArgs.dal.commitTransaction()
        if (failures.length > 0) {
            onError?.({errors: failures})
        } else {
            onSuccess?.(result ?? 'success')
        }
    }
}

const handleGeneralUploadError = <TOriginalData, TTransformed>(
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    errorMessage: string
): HandlerResult => {
    const {originalData, idMap, createExtArgs, failures, totalItems} = context

    Object.entries(idMap).forEach(([mappedId, value]) => {
        config.injectItem(createExtArgs, value, originalData[mappedId] as unknown as TTransformed)
    })

    failures.push(errorMessage)
    context.processedItems = totalItems

    checkUploadCompletionAndCallbacks(context)
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const handleSpecificItemUploadError = <TOriginalData, TTransformed>(
    mappedId: string,
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    errorMessage: string
): HandlerResult => {
    const {originalData, idMap, createExtArgs, failures} = context
    config.injectItem(createExtArgs, idMap[mappedId], originalData[mappedId] as unknown as TTransformed)
    failures.push(errorMessage)
    context.processedItems++
    checkUploadCompletionAndCallbacks(context)
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const processExtractedData = async <TOriginalData, TTransformed>(
    extractedData: any,
    mappedId: string,
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>
): Promise<HandlerResult> => {
    const transformedData = await config.transformData(extractedData)
    config.injectItem(context.createExtArgs, context.idMap[mappedId], transformedData)
    context.processedItems++
    checkUploadCompletionAndCallbacks(context, extractedData)
    return {status: HandlerStatus.COMPLETED}
}

const handleFileAfterSyncUpload = async <TOriginalData, TTransformed>(
    mappedId: string,
    url: string,
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    fileDescriptor: SyncImportFileResponse | null
) => {
    try {
        if (!fileDescriptor || fileDescriptor.file?.operationStatus !== OperationStatus.READY)
            return handleSpecificItemUploadError(mappedId, context, config, config.errorMessage)
        config.onItemUploadComplete?.(url)
        const extractedData = config.extractData(fileDescriptor)
        return await processExtractedData(extractedData, mappedId, context, config)
    } catch (error) {
        return handleSpecificItemUploadError(
            mappedId,
            context,
            config,
            `${config.errorMessage}, original error: ${error}`
        )
    }
}

const createUploadCallback = <TOriginalData, TTransformed>(
    mappedId: string,
    url: string,
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>
): ((fileId: string) => Handler) => {
    return (fileId: string) =>
        async (uploadData: PayloadData): Promise<HandlerResult> => {
            const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI
            try {
                const {status, output} = uploadData ?? {}
                const extractedData = config.extractData(output)

                if (status === PayloadStatus.COMPLETED && extractedData) {
                    config.onItemUploadComplete?.(url)
                    try {
                        return await processExtractedData(extractedData, mappedId, context, config)
                    } catch (error) {
                        return handleSpecificItemUploadError(
                            mappedId,
                            context,
                            config,
                            `${config.errorMessage}: ${error}`
                        )
                    }
                }

                if (status === PayloadStatus.FAILED && output?.reason === uploadTimeoutReason && fileId) {
                    logger.interactionStarted('checking-file-status', {extras: {fileId}})
                    const {status: fileStatus, fileDescriptor} = await checkFileStatus(context.serverFacade, fileId)
                    logger.interactionEnded('checking-file-status', {extras: {fileId, fileStatus}})
                    if (fileStatus === OperationStatus.READY) {
                        const retryExtractedData = config.extractData(fileDescriptor)
                        if (retryExtractedData) {
                            config.onItemUploadComplete?.(url)
                            return await processExtractedData(retryExtractedData, mappedId, context, config)
                        }
                    }
                    logger.captureError(new Error(`File is not ready even after polling`), {
                        extras: {fileId}
                    })
                }
            } catch (error: any) {
                logger.captureError(getReportableFromError(error, {errorType: ERRORS.IMAGES.UPLOAD}), {
                    tags: {fileId}
                })
                return handleSpecificItemUploadError(mappedId, context, config, `File is not ready`)
            }
            return handleSpecificItemUploadError(mappedId, context, config, config.errorMessage)
        }
}

export const uploadFilesWithHandlers = async <TOriginalData, TTransformed>(
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    items: Record<string, string>
): Promise<HandlerResult | void> => {
    try {
        const {fileUpload} = context.createExtArgs.extensionAPI as FileUploadExtApi
        const {handlers, urls} = Object.entries(items).reduce(
            (acc, [mappedId, url]) => {
                config.onItemUploadStart?.(url)
                acc.handlers.push(createUploadCallback(mappedId, url, context, config))
                acc.urls.push(url)
                return acc
            },
            {handlers: [] as ((fileId: string) => Handler)[], urls: [] as string[]}
        )
        await fileUpload.uploadFiles(handlers, urls, context.caller, config.timeOutInMS)
    } catch (error) {
        return handleGeneralUploadError(context, config, `trying to call upload failed: ${error}`)
    }
}

export const uploadFilesWithHandlersSync = async <TOriginalData, TTransformed>(
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    items: Record<string, string>
): Promise<HandlerResult | void> => {
    try {
        const {fileUpload} = context.createExtArgs.extensionAPI as FileUploadExtApi
        const {mediaType} = context

        const urlsWithMetaData: UploadFilesSyncObject[] = Object.entries(items).map(([mappedId, url]) => {
            config.onItemUploadStart?.(url)
            return {
                id: mappedId,
                url,
                mediaType,
                displayName: `ai-generated-${mediaType}`
            }
        })

        const fileDescriptors = await fileUpload.uploadFilesSync(urlsWithMetaData)
        const urlMap = Object.fromEntries(Object.entries(items))
        await Promise.all(
            fileDescriptors.map(async ({id: mappedId, response}) => {
                await handleFileAfterSyncUpload(mappedId, urlMap[mappedId], context, config, response)
            })
        )
    } catch (error) {
        return handleGeneralUploadError(context, config, `trying to call sync upload failed: ${error}`)
    }
}

export const uploadSingleFileWithHandler = <TOriginalData, TTransformed>(
    context: MediaBatchContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    mappedId: string,
    url: string
): Promise<HandlerResult | void> => {
    return uploadFilesWithHandlers(context, config, {[mappedId]: url})
}
